import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSetAtom } from 'jotai';
import dayjs from 'dayjs';
import { AxiosError } from 'axios';
import { RecordFilter } from '@/types/api';
import { requestSummarize } from '@/hooks/apiHooks';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import { isEmptyContent } from '@/utils/editor';
import { addPendingAiSummary } from '@/utils/aiAutoFill';
import { useRecorder } from '@/components/RecorderProvider';
import {
  DocumentAiSummariesState,
  OrganizeStatusState,
} from '@/recoil/atom/documents';

// 자동정리 고정 프롬프트 (프롬프트 선택 UI 없음)
const AUTO_PROMPT = '회의 내용을 자세히 정리해주세요.';
// 첨부(파일 업로드) 경로 디바운스 — 여러 개를 한꺼번에 올리면 파일마다
// 완료 시점이 달라서 요약 요청이 파일 수만큼 나가는 것을 막는다.
// 녹음은 한 번에 하나뿐이라 배치할 이유가 없어 이 대기를 타지 않는다(아래 즉시 경로).
const DEBOUNCE_MS = 1500;
// 완료/에러 신호 유실 대비 안전 타임아웃 (organizing 영구 잔류 방지)
const SAFETY_TIMEOUT = 10 * 60 * 1000;

/**
 * 녹음 파일 추가 시 자동 재정리 (기획 3.1/3.2).
 *
 * 트리거/완료는 기존 summarize 로직(`usePromptInput`의 `request()`)을 그대로 태운다.
 *  - POST /summarize → aiPipelineId → addPendingAiSummary 등록
 *  - content 채우기는 이미 동작 중인 useBatchPollingAiSummaries 가
 *    완료 감지 후 onCompleted(handleAiOnCreate) 로 처리한다.
 *
 * 추가로:
 *  - 출처별 트리거 시점: 녹음은 즉시, 첨부(파일 업로드)는 DEBOUNCE_MS 대기.
 *  - 기존 AI 정리 본문이 있으면 덮어쓰기 Alert 후 실행, 없으면 바로 실행.
 *  - "정리 중" UI(탭 전환/활성화/스켈레톤)는 로컬 `isOrganizing` 으로 노출한다.
 *    트리거 즉시 true, 종료(finish)는 배치폴링의 onCompleted/onError(또는 안전 타임아웃)에서 호출.
 *
 * 프롬프트만 UI 대신 고정값을 사용한다. 작성자(isOwner)만 동작.
 */
const useAutoSummarize = ({
  docId,
  isOwner,
}: {
  docId: string;
  isOwner: boolean;
}) => {
  const { watch, getValues } = useFormContext<RecordFilter>();
  const { showError } = useSnackbar();
  const dialog = useDialog();
  const setAiSummaries = useSetAtom(DocumentAiSummariesState);
  const setOrganizeStatus = useSetAtom(OrganizeStatusState);

  const recordings = watch('recordings');
  const completedCount = recordings.filter(
    (r) => r.state === 'complete',
  ).length;

  // 녹음 출처 판별 신호 — 녹음 업로드가 끝나면 RecorderProvider 가 state.recordingData
  // 를 채운다(첨부 업로드는 이 값을 건드리지 않는다). Recording 타입에 출처 필드를
  // 넣지 않는 이유: recordings 는 서버에 저장/조회되는 구조(RecordFilter)라 서버가
  // 모르는 필드는 왕복에서 유실된다.
  const { state: recorderState } = useRecorder();
  const recordedUuid = recorderState.recordingData?.uuid;
  const recordedDocId = recorderState.docId;

  const baselineRef = useRef<number | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 녹음으로 업로드된 uuid 들. 이 uuid 가 recordings 에 붙는 순간 디바운스를 건너뛴다.
  const recordedUuidsRef = useRef<Set<string>>(new Set());
  // "정리 중" UI 상태 (탭 전환/활성화/스켈레톤 트리거)
  const [isOrganizing, setIsOrganizing] = useState(false);

  // organizing 종료 (완료/에러/타임아웃 공통).
  // 완료·에러 신호는 index.tsx 의 배치폴링 onCompleted/onError 에서 이 함수를 호출한다.
  const finish = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOrganizing(false);
  }, []);

  // 기존 request() 와 동일하게 summarize 요청을 실행한다.
  const triggerSummarize = useCallback(async () => {
    const uuids = getValues('recordings')
      .filter((file) => file.uuid !== 'uploading')
      .map((file) => ({ uuid: file.uuid }));
    // 에디터 입력(content 파라미터)에는 작성자 메모(memo)를 넣는다.
    // TalkFlow 에서 content 필드는 AI 정리 "결과"(곧 덮어쓸 대상)이므로 재요약 입력으로 부적절.
    const memoData = getValues('memo');
    // 정리할 입력(녹음/메모)이 전혀 없으면 실행하지 않는다.
    // (수동 재정리는 녹음 없이 메모만으로도 실행 가능 — PORTAL-12850)
    if (uuids.length === 0 && (!memoData || isEmptyContent(memoData))) return;

    // 트리거 즉시 organizing 표시(탭 전환/스켈레톤) — POST 응답을 기다리지 않는다.
    // 재시도(에러 배너 → 재시도) 대비, 이전 에러 상태를 loading 으로 덮어쓴다.
    // (SSE organize.start 가 오면 startedAt 만 갱신됨)
    setIsOrganizing(true);
    setOrganizeStatus((prev) => ({
      ...prev,
      [docId]: { status: 'loading', startedAt: Date.now() },
    }));
    try {
      const prompt = AUTO_PROMPT;
      const params = {
        recordingFiles: uuids,
        prompt,
        ...(memoData && !isEmptyContent(memoData) ? { content: memoData } : {}),
        attendeeNames: [
          ...getValues('internal').map(
            (item) => `${item.name} ${item.positionName}`,
          ),
          ...getValues('external'),
        ],
        summarizeType: 'both' as const,
      };

      const { data: aiPipelineId } = await requestSummarize(docId, params);

      // 사용자가 요청한 AI 요약 ID를 localStorage에 저장 (자동 채우기용)
      addPendingAiSummary(docId, aiPipelineId);

      setAiSummaries((prev) => ({
        ...prev,
        [aiPipelineId]: {
          id: aiPipelineId,
          docId,
          prompt,
          content: '',
          contentDom: '',
          createdAt: dayjs(),
          uiState: 'loading',
          summarizeType: 'both',
        },
      }));

      // 완료/에러 신호 유실 대비 안전 타임아웃
      timeoutRef.current = setTimeout(finish, SAFETY_TIMEOUT);
    } catch (e) {
      finish();
      // 요청 자체가 실패(SSE 미도래) → 본문 organizing 상태를 에러로 전환해
      // 에러 배너(재시도 버튼)를 노출한다. 배너 문구는 고정이므로 백엔드 메시지는 토스트로도 안내.
      setOrganizeStatus((prev) => ({
        ...prev,
        [docId]: { status: 'error', startedAt: Date.now() },
      }));
      let message = 'AI 정리 요청에 실패했습니다.';
      if (e instanceof AxiosError && e.response?.data?.message) {
        message = e.response.data.message;
      }
      showError({ message });
    }
  }, [docId, getValues, setAiSummaries, setOrganizeStatus, showError, finish]);

  // 파일 추가 후 실제 트리거 판단: 기존 정리 본문 있으면 덮어쓰기 Alert, 없으면 바로 실행
  const requestAutoSummarize = useCallback(() => {
    // 자동정리는 작성자만 실행 (앱 OPEN_AI 직접 호출 경로 방어. 자동 effect 는 이미 isOwner 가드)
    if (!isOwner) return;
    if (isEmptyContent(getValues('content'))) {
      triggerSummarize();
      return;
    }
    dialog.show({
      message:
        '추가된 녹음 파일이 있어요.\n기존 회의록을 덮어쓰고 다시 정리할까요?',
      confirmButtonText: '덮어쓰기',
      cancelButtonText: '정리 안 함',
      onConfirm: () => triggerSummarize(),
    });
  }, [dialog, getValues, triggerSummarize, isOwner]);

  // 본문 에디터 툴바 '재정리 요청' 버튼의 수동 트리거 (PORTAL-12850).
  // 자동정리와 동일한 summarize 실행 경로를 재사용하고, Alert 문구만 수동 맥락에 맞춘다.
  const requestManualSummarize = useCallback(() => {
    // 버튼은 작성자 에디터에만 노출되지만, 자동정리 진입점과 동일하게 방어한다
    if (!isOwner) return;
    if (isEmptyContent(getValues('content'))) {
      triggerSummarize();
      return;
    }
    dialog.show({
      message: '기존 회의록을 덮어쓰고 다시 정리할까요?',
      confirmButtonText: '덮어쓰기',
      cancelButtonText: '취소',
      onConfirm: () => triggerSummarize(),
    });
  }, [dialog, getValues, triggerSummarize, isOwner]);

  // 녹음 업로드 완료 신호 기록 (아직 recordings 에 붙기 전 단계).
  // 업로드 성공 → recordingData 세팅 → (문서 연결 API) → recordings 에 추가 순서라,
  // 여기서 uuid 만 기억해 두고 실제 트리거는 recordings 에 붙은 뒤에 한다.
  // recordingData 시점에 바로 요약을 요청하면 그 녹음이 uuids 목록에서 빠진다.
  // (아래 개수 감지 effect 보다 먼저 선언 — 두 변화가 한 커밋에 몰려도 신호가 먼저 기록된다)
  useEffect(() => {
    if (!recordedUuid) return;
    // 다른 문서에서 녹음 중인 경우는 이 문서의 신호가 아니다
    if (recordedDocId !== docId) return;
    recordedUuidsRef.current.add(recordedUuid);
  }, [recordedUuid, recordedDocId, docId]);

  // 완료 파일 개수 증가 감지 → 녹음이면 즉시, 첨부면 디바운스 트리거
  useEffect(() => {
    if (!isOwner) return;

    // 초기 로드 시 baseline 설정 (기존 녹음으로 오트리거 방지)
    if (baselineRef.current === null) {
      baselineRef.current = completedCount;
      return;
    }

    if (completedCount > baselineRef.current) {
      baselineRef.current = completedCount;

      // 늘어난 완료 파일 중 녹음 신호 uuid 가 있으면 녹음 경로 → 즉시 트리거.
      // 녹음은 한 번에 하나라 배치할 필요가 없고, 1.5초를 기다리는 사이 모달을
      // 닫으면 트리거가 유실된다.
      const isFromRecording = getValues('recordings').some(
        (file) =>
          file.state === 'complete' && recordedUuidsRef.current.has(file.uuid),
      );

      if (isFromRecording) {
        // 중복 방지 1) 대기 중인 디바운스 취소 — 즉시 요청이 현재 recordings 전체를
        // 담아 나가므로, 같은 건으로 요청이 두 번 나가지 않는다.
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
          debounceRef.current = null;
        }
        // 중복 방지 2) 신호 소비 — 이후 첨부로 개수가 늘어도 즉시 경로를 타지 않는다.
        recordedUuidsRef.current.clear();
        requestAutoSummarize();
        return;
      }

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(requestAutoSummarize, DEBOUNCE_MS);
    } else if (completedCount < baselineRef.current) {
      // 삭제 등으로 감소 → baseline 만 갱신
      baselineRef.current = completedCount;
    }
  }, [completedCount, isOwner, requestAutoSummarize, getValues]);

  // docId 변경 시 초기화. 대기 중인 타이머는 cleanup 에서 지운다 — 문서가 바뀔 때뿐 아니라
  // 화면이 내려갈 때(PC 는 key={docId} 재마운트)도. 폼(FormProvider)은 key 바깥이라 이어지므로,
  // 살아남은 첨부 디바운스가 새 문서의 녹음·메모·참석자를 읽어 이전 문서로 정리를 보내고
  // 새 문서 본문이 비어 있으면 확인 없이 실행됐다 (PORTAL-12584 재QA, AI 리뷰 [높음])
  useEffect(() => {
    baselineRef.current = null;
    recordedUuidsRef.current.clear();
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      finish();
    };
  }, [docId, finish]);

  // 에러 배너의 "다시 시도" — 확인 없이 곧바로 재정리(정리 실패 후엔 덮어쓸 본문이 없다)
  // requestAutoSummarize: 녹음 업로드 시와 동일한 자동정리 진입점(기존 본문 있으면 덮어쓰기 확인).
  // 앱 공유(OPEN_AI) 트리거가 이 진입점을 그대로 태운다.
  // requestManualSummarize: 툴바 '재정리 요청' 버튼의 수동 진입점 (PORTAL-12850).
  return {
    isOrganizing,
    finish,
    retryOrganize: triggerSummarize,
    requestAutoSummarize,
    requestManualSummarize,
  };
};

export default useAutoSummarize;
